import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Search, RefreshCw } from 'lucide-react';
import Layout from '../Layout';
import { fetchActivityLogs } from '../../api';

const ACTIONS = [
  'LOGIN', 'LOGIN_FAILED', 'PAGE_VISIT', 'CREATE_ADMIN',
  'CREATE_VEHICLE_ENTRY', 'VEHICLE_EXIT', 'ADD_CHARA_TYPE', 'DELETE_CHARA_TYPE',
];

const ACTION_LABELS = {
  LOGIN: 'Login',
  LOGIN_FAILED: 'Login Failed',
  PAGE_VISIT: 'Page Visit',
  CREATE_ADMIN: 'Created Admin',
  CREATE_VEHICLE_ENTRY: 'Vehicle Entry Added',
  VEHICLE_EXIT: 'Vehicle Exit',
  ADD_CHARA_TYPE: 'Chara Type Added',
  DELETE_CHARA_TYPE: 'Chara Type Deleted',
};

// created_at_ist is already stored as IST — just format it for display.
const fmtIST = (dt) => {
  if (!dt) return '—';
  const d = new Date(dt.replace(' ', 'T'));
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  }) + ' IST';
};

function ActivityLogsPage({ userRole, userEmail }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (emailFilter.trim()) params.email = emailFilter.trim();
      if (actionFilter) params.action = actionFilter;
      if (dateFrom) params.from = `${dateFrom} 00:00:00`;
      if (dateTo) params.to = `${dateTo} 23:59:59`;
      const { data } = await fetchActivityLogs(params);
      setLogs(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [emailFilter, actionFilter, dateFrom, dateTo]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  return (
    <Layout userRole={userRole} userEmail={userEmail}
      pageTitle="Activity Logs" pageSubtitle="See who did what, and when (IST)">

      <div className="section-card" style={{ marginBottom: 20 }}>
        <div className="section-header">
          <div className="section-title"><Search size={15} />Filters</div>
        </div>
        <div style={{ padding: '14px 20px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>User Email</div>
            <input type="text" placeholder="Search email…" value={emailFilter}
              onChange={e => setEmailFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none', minWidth: 200 }} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>Action</div>
            <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none', minWidth: 170 }}>
              <option value="">All Actions</option>
              {ACTIONS.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>From</div>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none' }} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 }}>To</div>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13.5, outline: 'none' }} />
          </div>
          <button className="btn-primary-dash" onClick={loadLogs} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </button>
        </div>
      </div>

      <div className="section-card">
        <div className="section-header">
          <div className="section-title"><ClipboardList size={16} />Activity ({logs.length})</div>
          <button className="btn-ghost" onClick={loadLogs}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
        <div className="modern-table-wrap">
          <table className="modern-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date &amp; Time (IST)</th>
                <th>User</th>
                <th>Role</th>
                <th>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280' }}>
                    <div className="spinner-border spinner-border-sm" style={{ marginRight: 8, color: '#1E5FA8' }} />
                    Loading…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280', fontSize: 14 }}>
                    No activity found for the selected filters.
                  </td>
                </tr>
              ) : logs.map((log, i) => (
                <tr key={log.id || i}>
                  <td style={{ color: '#9CA3AF', fontSize: 12 }}>{i + 1}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtIST(log.created_at_ist)}</td>
                  <td><strong>{log.user_email}</strong></td>
                  <td>
                    <span className={`badge-pill ${log.user_role === 'superadmin' ? 'danger' : 'primary'}`}>
                      {log.user_role === 'superadmin' ? 'Super Admin' : 'Admin'}
                    </span>
                  </td>
                  <td>{ACTION_LABELS[log.action] || log.action}</td>
                  <td style={{ color: '#6B7280' }}>{log.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default ActivityLogsPage;
